/* ORYX App 2.0
 *
 * Created by ORYX Movement Solutions © 2022
 * ==================================================================
 *
 * Login form Component, including the logo.
 */

import { analytics } from '@/firebaseConfig/firebaseConfig';
import { IonInputCustomEvent } from '@ionic/core';
import { IonCol, IonGrid, IonImg, IonItem, IonLabel, IonList, IonRow, IonText } from '@ionic/react';
import React, { useEffect, useState } from 'react';

import { Button } from '@/components/Button';
import styles from '@/components/Forms/loginForm.module.css';
import { Input } from '@/components/Input';
import { toast } from '@/components/Toaster/Toast';

import { mergeClassNames } from '@/utils/mergeClassNames';

import { useAuthContext } from '@/contexts/authContext';

import Logo from '@/assets/oryx-logo.svg';

export const LoginForm = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);

  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  // const [isLoading, setIsLoading] = useState(false);

  const SHOW_FORGOT = true;

  const { signInWithEmailAndPassword, state, clearError } = useAuthContext();
  const isLoading = state.isLoading;

  useEffect(() => {
    if (state.error) {
      if (import.meta.env.MODE !== 'production') console.log(state.error);
      toast({
        variant: 'error',
        title: 'Error signing in',
        description: 'Incorrect email or password. Please try again.',
      });
    }
  }, [state]);

  useEffect(() => {
    //getting the OOB code from the URL params, this oob code is sent in an email (embedded in a link)
    const urlParams = new URLSearchParams(window.location.search);

    try {
      const newResetCode = urlParams.get('passwordChanged')?.toString();
      if (newResetCode === 'true') {
        toast({
          variant: 'success',
          title: 'Your password has been updated',
          description:
            'Your password has been changed successfully. Please log in to your account using your new password.',
        });
      }

      const CLEAR_URL_PARAMS_DELAY = 8_000;

      // After 8 seconds we clear the URL params, since the modal will have been dismissed by then
      setTimeout(() => {
        // Clear the URL params
        window.history.pushState({}, document.title, '/');
      }, CLEAR_URL_PARAMS_DELAY);
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Error signing in',
        description: 'Please try again.',
      });
    }
  }, []);

  async function login(e: React.FormEvent) {
    analytics.logEvent('login');
    e.preventDefault();
    setFormSubmitted(true);
    if (!!email && !!password) {
      // setIsLoading(true);
      await signInWithEmailAndPassword(email, password);
      // setIsLoading(false);
    }
  }

  const handleEmail = (e: any) => {
    setEmail(e.detail.value);
  };

  const handlePassword = (e: any) => {
    setPassword(e.detail.value);
  };

  function handleFocusEvent(ev: IonInputCustomEvent<FocusEvent>) {
    ev.target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <form className={styles['login-form']} noValidate onSubmit={login}>
      <IonGrid className={styles['no-padding']} fixed id='login-form'>
        <div>
          {import.meta.env.MODE !== 'production' && (
            <>
              <IonRow className={mergeClassNames('warning', styles.dev)}>
                <IonCol>
                  <IonText style={{ color: '#ffffff' }}>{import.meta.env.MODE} environment!</IonText>
                </IonCol>
              </IonRow>
            </>
          )}

          <IonRow>
            <IonCol className={styles['no-padding']} size='12'>
              <IonImg className={styles.logo} src={Logo}></IonImg>
            </IonCol>
          </IonRow>
        </div>
        <div className={styles.main} id='focus-target'>
          <IonRow>
            <IonCol className={styles.copy} size='12'>
              <IonText className={styles.heading}>Sign in to Oryx</IonText>
              <IonText className={styles.description}>Please sign in to your account</IonText>
            </IonCol>
          </IonRow>

          <IonRow className={styles['no-padding']}>
            <IonCol class={styles['no-padding']} size='12'>
              <IonList class={mergeClassNames(styles['no-padding'], styles.list)} mode='md'>
                <Input
                  label='Email'
                  label-placement='stacked'
                  name='email'
                  onIonChange={handleEmail}
                  onIonFocus={handleFocusEvent}
                  type='email'
                />

                <Input
                  label='Password'
                  label-placement='stacked'
                  name='password'
                  onIonChange={handlePassword}
                  onIonFocus={handleFocusEvent}
                  type='password'
                />

                {formSubmitted && (!email || !password) && (
                  <IonText color='warning'>
                    <p className='ion-padding-start'>Email and password are required.</p>
                  </IonText>
                )}
              </IonList>
            </IonCol>
          </IonRow>
          <IonRow class={styles['no-padding']}>
            <IonCol class={mergeClassNames(styles['button-wrapper'], styles['no-padding'])} size='12'>
              <Button className={styles.button} isFullWidth isLoading={isLoading} type='submit' variant='primary'>
                Sign in
              </Button>
            </IonCol>
          </IonRow>
        </div>
        {SHOW_FORGOT && (
          <IonRow class={styles['forgot-wrapper']}>
            <IonCol size='12'>
              <IonItem className={styles.forgot} detail={false} href='/resetPassword' lines='none'>
                <IonLabel class={styles.forgot}>
                  <div className={styles['forgot-text']}>Forgot password?</div>
                </IonLabel>
              </IonItem>
            </IonCol>
          </IonRow>
        )}
      </IonGrid>
    </form>
  );
};
